<script setup>
import { onMounted, ref } from 'vue'
import { donationProducts, paymentServices } from '../../../../configuration'
import { useAnalytics } from '@composables/use-analytics'

const analyticsService = useAnalytics()
const selectedProduct = ref(donationProducts[1])
const selectedPaymentService = ref(null) // 'stripe' | 'paypal'

function selectProduct(product) {
    analyticsService.donationUpsellAmountSelected({ amount: product.amount })
    selectedProduct.value = product
}

function checkoutDonation(paymentService) {
    analyticsService.donationUpsellPaymentSelected({
        amount: selectedPaymentService.value?.amount,
        method: paymentService,
    })
    if (!selectedProduct.value) return

    selectedPaymentService.value = paymentService
    if (!selectedPaymentService.value) return

    window.open(selectedProduct.value[selectedPaymentService.value].url, '_self')
}

onMounted(() => {
    analyticsService.donationUpsellInitiated()
})
</script>

<template>
    <div class="donation-container">
        <BaseSection>
            <h3 class="title">Donate</h3>
            <p>
                We hope our music brings you joy every day. Please consider giving back to support us so we can continue to
                bring you great music!
            </p>
            <p>
                * Note: Donations do not include Premium membership so 100% of your donation goes towards running FRISKY
                service.
            </p>
            <div class="actions">
                <div class="amount-buttons">
                    <BaseButton
                        v-for="product of donationProducts"
                        :key="'donation-amount' + product.amount"
                        :class="['amount-button', { selected: product.amount === selectedProduct.amount }]"
                        button-type="blue"
                        @click="selectProduct(product)"
                    >
                        DONATE ${{ product.amount }}
                    </BaseButton>
                </div>
            </div>
        </BaseSection>
        <BaseSection
            v-if="selectedProduct"
        >
            <h1 class="services-title">Select a Donation Method</h1>
            <div class="services">
                <div
                    v-for="service of paymentServices"
                    :key="'payment-service' + service.alias"
                    :class="['service', { selected: service.alias === selectedPaymentService }]"
                    :title="service.title"
                    :aria-label="service.title"
                    @click="checkoutDonation(service.alias)"
                >
                    <div class="service-details">
                        <div>
                            <p class="service-title">Donate <strong>${{ selectedProduct.amount }}</strong> with {{ service.title }}</p>
                            <div>
                                <p
                                    v-if="service.description"
                                    class="service-description"
                                >
                                    {{ service.description }}
                                </p>
                            </div>
                        </div>
                        <BaseIcon
                            class="icon-payment"
                            :name="service.icon"
                        />
                    </div>
                </div>
            </div>
        </BaseSection>
    </div>
</template>

<style lang="scss" scoped>
@use '@design' as *;

.donation-container {
    padding: $s-m $s-m $s-xxxl;
    margin-top: $s-m;
    background-color: $c-surface-raised;
}

.title {
    margin: 0;

    @include h6;

    @include desktop {
        @include h5;
    }
}

.actions {
    margin-top: $s-xxxl;
}

.amount-buttons {
    display: flex;
    flex-direction: column;
    gap: $s-xl;
    align-items: center;
    justify-content: center;
}

.amount-button {
    margin: 0;

    &.selected {
        background: $c-frisky-channel-grad;
    }
}

.services-title {
    @extend %h6;
}

.services {
    display: flex;
    flex-direction: column;
}

.service-details {
    display: flex;
    gap: $s-m;
    align-items: center;
    justify-content: space-between;
    text-align: left;
}

.service-title {
    margin: 0;
    font-weight: normal;

    @extend %contentTitleM;
}

.service-description {
    margin: 0;

    @extend %contentSubtitleM;
}

.service {
    position: relative;
    width: 500px;
    max-width: 100%;
    padding: $s-m $s-xxl;
    cursor: pointer;
    background: $c-surface-raised;
    border-radius: $button-border-radius;

    + .service {
        margin-top: $s-m;
    }

    &.selected {
        .service-title {
            font-weight: 800;
        }
    }
}

.icon-payment {
    flex-shrink: 0;
    width: 35px;
    height: 35px;
    color: $c-white;
}

@include tablet {
    .amount-buttons {
        flex-direction: row;
        gap: $s-m;
        justify-content: flex-start;
    }
}

</style>
